import { useId, useState } from 'react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import { ToolbarButton } from '@mui/x-data-grid-premium';
import { useLocalization } from '@/hooks/useLocalization';
import { MimeType } from '@/utils/fileValidation';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';
import {
  buildAccountsExportFilename,
  type AccountsExportFormat,
  type AccountsExportToolbarConfig,
} from './accountsExport';
import {
  encodeAccountsExportCsv,
  encodeAccountsExportXlsx,
} from './accountsExportEncoders';

interface AccountsExportMenuButtonProps {
  config: AccountsExportToolbarConfig;
  touchFriendly?: boolean;
}

export function AccountsExportMenuButton({
  config,
  touchFriendly = false,
}: AccountsExportMenuButtonProps) {
  const { t } = useLocalization();
  const buttonId = useId();
  const menuId = useId();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const isOpen = anchorElement !== null;
  const exportLabel = t('export', 'Export');
  const touchTargetStyles = touchFriendly
    ? { minHeight: 44, minWidth: 44 }
    : undefined;

  const closeMenu = (): void => {
    setAnchorElement(null);
  };

  const download = async (format: AccountsExportFormat): Promise<void> => {
    const table = config.buildTable();
    const isCsv = format === 'csv';
    const blob = isCsv
      ? encodeAccountsExportCsv(table)
      : encodeAccountsExportXlsx(table);
    const mimeType = isCsv ? MimeType.Csv : MimeType.Xlsx;
    const filename = buildAccountsExportFilename(config.uiDate, format);

    closeMenu();
    await triggerBlobDownload(blob, filename, mimeType);
  };

  return (
    <>
      <Tooltip
        title={exportLabel}
        disableInteractive={isOpen}
      >
        <ToolbarButton
          id={buttonId}
          aria-label={exportLabel}
          style={touchTargetStyles}
          aria-controls={isOpen ? menuId : undefined}
          aria-haspopup='menu'
          aria-expanded={isOpen ? 'true' : undefined}
          onClick={(event) => {
            setAnchorElement(event.currentTarget);
          }}
        >
          <FileDownloadIcon fontSize='small' />
        </ToolbarButton>
      </Tooltip>

      <Menu
        id={menuId}
        anchorEl={anchorElement}
        open={isOpen}
        onClose={closeMenu}
        slotProps={{
          list: {
            'aria-labelledby': buttonId,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            void download('csv');
          }}
        >
          {t('grid.downloadAsCsv', 'Download as CSV')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            void download('xlsx');
          }}
        >
          {t('grid.downloadAsExcel', 'Download as Excel')}
        </MenuItem>
      </Menu>
    </>
  );
}
